import * as Sentry from '@sentry/react';
import type { ProductInput } from '@vy/types';
import { NewPlanSchema, UPLIFT_DURATION_ERROR_MESSAGE, violatesUpliftDurationRule } from '@vy/types';
import { oz, vySerialize, withDefault } from '@vy/utils';
import type { PlanInputForm } from '../pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { isProRated } from '../pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/create-plan-form-sub-types';
import { mapProductFormInputToProduct } from '../services/map-product-form-input-to-product';
import { getDisplayableZodParseErrors } from './serialize-zod-parse-errors';
import { stripIncompleteDiscounts } from './strip-incomplete-discounts';
import { userMessage } from './vy-toasts';

type ParsePlanInputOptions = {
  onFailure?: (errorContext: string, planInput: PlanInputForm, formattedErrors: string) => void;
  errorContext?: string;
  allowEmptyProducts?: boolean;
  // Also enforce rules the server applies only when the plan is created together
  // with a contract (NewContractSchemaWithPlan), so they fail here instead of as
  // an opaque API error.
  withContractRules?: boolean;
};

const NewPlanSchemaAllowEmptyProducts = NewPlanSchema.extend({
  products: NewPlanSchema.shape.products.min(0),
});

function withContractLevelRules<Schema extends typeof NewPlanSchema | typeof NewPlanSchemaAllowEmptyProducts>(
  schema: Schema,
) {
  return schema.superRefine((plan, ctx) => {
    if (violatesUpliftDurationRule(plan)) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        message: UPLIFT_DURATION_ERROR_MESSAGE,
        path: ['products'],
      });
    }
  });
}

export const parsePlanInput = (rawPlanInput: PlanInputForm, options: ParsePlanInputOptions = {}) => {
  const planInput = stripIncompleteDiscounts(rawPlanInput);
  const fixedProducts = withDefault(planInput.fixedProducts, []);
  const usageProducts = withDefault(planInput.usageProducts, []);
  const promotionProducts = withDefault(planInput.promotionProducts, []);
  const productInputs = [...fixedProducts, ...usageProducts, ...promotionProducts];
  const products = productInputs.map<ProductInput>((product) => mapProductFormInputToProduct(product));

  const billingData = {
    ...planInput.billingData,
    proRated: planInput.proRataSetting ? isProRated(planInput.proRataSetting) : undefined,
  };

  const { allowEmptyProducts = false, withContractRules = false } = options;
  const baseSchema = allowEmptyProducts ? NewPlanSchemaAllowEmptyProducts : NewPlanSchema;
  const schema = withContractRules ? withContractLevelRules(baseSchema) : baseSchema;

  const parseResult = schema.safeParse({
    ...planInput,
    billingData,
    products,
  });

  if (!parseResult.success) {
    const { onFailure = onParseFailure, errorContext = 'Plan validation' } = options;
    const formattedErrors = getDisplayableZodParseErrors(parseResult.error);

    onFailure(errorContext, planInput, formattedErrors);
  }

  return parseResult;
};

function onParseFailure(errorContext: string, planInputErrors: PlanInputForm, formattedErrors: string) {
  userMessage('error', `Validation Errors:\n${formattedErrors}`);

  Sentry.captureException(
    new Error(`${errorContext} errors: ${formattedErrors}, form data: ${vySerialize(planInputErrors)}`),
  );
}
